import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../api-client";

// ─── Types ───────────────────────────────────────────────────────────────

export type GatheringVenueType =
  | "BOOKSTORE"
  | "CAFE"
  | "LIBRARY"
  | "PARK"
  | "HOME"
  | "OTHER";

export type RsvpStatus = "GOING" | "MAYBE" | "CANT";

export interface ListedGathering {
  id: string;
  title: string;
  description: string | null;
  date: string;
  endDate: string | null;
  venueType: GatheringVenueType | null;
  venueName: string | null;
  neighborhood: string | null;
  address: string | null;
  capacity: number | null;
  discussionFocus: string | null;
  cancelledAt: string | null;
  publiclyVisible: boolean;
  readingRoom: {
    id: string;
    name: string | null;
    book: { title: string } | null;
  } | null;
  createdBy: { id: string; displayName: string | null; image: string | null };
  rsvpCounts: Partial<Record<RsvpStatus, number>>;
  userRsvp: RsvpStatus | null;
  createdAt: string;
}

export interface GatheringRsvp {
  id: string;
  gatheringId: string;
  userId: string | null;
  status: RsvpStatus | null;
  email: string | null;
  name: string | null;
  cancelledAt: string | null;
  createdAt: string;
  user: {
    id: string;
    displayName: string | null;
    image: string | null;
  } | null;
}

export interface GatheringDetail {
  id: string;
  bookClubId: string;
  title: string;
  description: string | null;
  date: string;
  endDate: string | null;
  venueType: GatheringVenueType | null;
  venueName: string | null;
  neighborhood: string | null;
  address: string | null;
  capacity: number | null;
  discussionFocus: string | null;
  cancelledAt: string | null;
  publiclyVisible: boolean;
  // Phase 4 PR7 (#296) — surfaced from the parent club so the cockpit knows
  // whether to render the publiclyVisible checkbox in edit mode.
  clubDiscoverable: boolean;
  readingRoomId: string | null;
  readingRoom: {
    id: string;
    name: string | null;
    book: { title: string } | null;
  } | null;
  createdBy: { id: string; displayName: string | null; image: string | null };
  rsvps: GatheringRsvp[];
}

export interface ListGatheringsResponse {
  gatherings: ListedGathering[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ClubReadingRoom {
  id: string;
  name: string | null;
  status: string;
  currentSegment?: number;
  totalSegments?: number;
  memberCount?: number;
  book: {
    id: string;
    title: string;
    author?: string | null;
    coverUrl: string | null;
  } | null;
}

export interface ClubDetails {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  city: string | null;
  neighborhood: string | null;
  isLocalClub: boolean;
  // Phase 4 PR7 (#296) — staff-set discovery flag. The cockpit gathering form
  // only renders the publiclyVisible checkbox when this is true.
  discoverable: boolean;
  memberCount: number;
  readingRooms: ClubReadingRoom[];
}

export interface GatheringInput {
  title: string;
  description?: string;
  date: string;
  endDate?: string;
  venueType?: GatheringVenueType;
  venueName?: string;
  neighborhood?: string;
  address?: string;
  capacity?: number;
  discussionFocus?: string;
  readingRoomId?: string;
  publiclyVisible?: boolean;
}

// ─── Query keys ──────────────────────────────────────────────────────────

export const gatheringsListKey = (clubId: string) =>
  ["clubs", clubId, "gatherings", "list"] as const;
export const gatheringDetailKey = (clubId: string, gatheringId: string) =>
  ["clubs", clubId, "gatherings", "detail", gatheringId] as const;
export const clubReadingRoomsKey = (clubId: string) =>
  ["clubs", clubId, "reading-rooms"] as const;
export const clubDetailsKey = (clubId: string) =>
  ["clubs", clubId, "details"] as const;

// ─── Queries ─────────────────────────────────────────────────────────────

interface ListOptions {
  upcoming?: boolean;
}

export function useClubGatherings(
  clubId: string | undefined,
  options: ListOptions = {},
) {
  const { upcoming = false } = options;
  return useQuery<ListGatheringsResponse>({
    queryKey: clubId
      ? [...gatheringsListKey(clubId), { upcoming }]
      : ["clubs", "_none", "gatherings", "list"],
    enabled: Boolean(clubId),
    queryFn: () => {
      const params = new URLSearchParams({
        upcoming: String(upcoming),
        limit: "50",
      });
      return getApiClient().get<ListGatheringsResponse>(
        `/clubs/${clubId}/gatherings?${params.toString()}`,
      );
    },
    staleTime: 10 * 1000,
  });
}

export function useGathering(
  clubId: string | undefined,
  gatheringId: string | undefined,
) {
  return useQuery<GatheringDetail>({
    queryKey:
      clubId && gatheringId
        ? gatheringDetailKey(clubId, gatheringId)
        : ["clubs", "_none", "gatherings", "detail", "_none"],
    enabled: Boolean(clubId && gatheringId),
    queryFn: () =>
      getApiClient().get<GatheringDetail>(
        `/clubs/${clubId}/gatherings/${gatheringId}`,
      ),
    staleTime: 10 * 1000,
  });
}

interface ClubWithRoomsResponse {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  city: string | null;
  neighborhood: string | null;
  isLocalClub: boolean | null;
  discoverable?: boolean | null;
  members?: { userId: string }[];
  readingRooms: ClubReadingRoom[];
}

/**
 * Fetches a club's cockpit-relevant details from `GET /clubs/:id` — name,
 * reading rooms (for the optional gathering link), and `isLocalClub` (gathering
 * routes require a local club).
 */
export function useClubDetails(clubId: string | undefined) {
  return useQuery<ClubDetails>({
    queryKey: clubId ? clubDetailsKey(clubId) : ["clubs", "_none", "details"],
    enabled: Boolean(clubId),
    queryFn: async () => {
      const club = await getApiClient().get<ClubWithRoomsResponse>(
        `/clubs/${clubId}`,
      );
      return {
        id: club.id,
        name: club.name,
        description: club.description ?? null,
        image: club.image ?? null,
        city: club.city ?? null,
        neighborhood: club.neighborhood ?? null,
        isLocalClub: Boolean(club.isLocalClub),
        discoverable: Boolean(club.discoverable),
        memberCount: club.members?.length ?? 0,
        readingRooms: club.readingRooms ?? [],
      };
    },
    staleTime: 30 * 1000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────

export function useCreateGathering(clubId: string) {
  const qc = useQueryClient();
  return useMutation<GatheringDetail, Error, GatheringInput>({
    mutationFn: (input) =>
      getApiClient().post<GatheringDetail>(
        `/clubs/${clubId}/gatherings`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gatheringsListKey(clubId) });
    },
  });
}

export function useUpdateGathering(clubId: string, gatheringId: string) {
  const qc = useQueryClient();
  return useMutation<GatheringDetail, Error, Partial<GatheringInput>>({
    mutationFn: (input) =>
      getApiClient().patch<GatheringDetail>(
        `/clubs/${clubId}/gatherings/${gatheringId}`,
        input,
      ),
    onSuccess: (updated) => {
      qc.setQueryData(gatheringDetailKey(clubId, gatheringId), updated);
      qc.invalidateQueries({ queryKey: gatheringsListKey(clubId) });
    },
  });
}

export function useCancelGathering(clubId: string, gatheringId: string) {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, void>({
    mutationFn: () =>
      getApiClient().delete<{ success: boolean }>(
        `/clubs/${clubId}/gatherings/${gatheringId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gatheringsListKey(clubId) });
      qc.invalidateQueries({
        queryKey: gatheringDetailKey(clubId, gatheringId),
      });
    },
  });
}
